/**
 * ManagerHandoffCard — surfaces the EXISTING `sheet.manager_notes`
 * field from /api/zone-sheets/{date} on the Manager Dashboard as a
 * pinned, read-only handoff card. Per user 2026-06-13: opening,
 * midday, and closing managers need to see what the previous manager
 * left them BEFORE they hunt through the Zone Sheet.
 *
 * Wave A keeps this read-only with a clear "Edit on Zone Sheet" CTA.
 * Editing happens on the Zone Sheet where it already works — we are
 * NOT making the dashboard a full editor in Wave A.
 *
 * No new collection. No new endpoint. The dashboard fetches the same
 * zone sheet doc every section reads from, and passes the relevant
 * fields in as props.
 */
import { Note, ArrowRight, Clock } from "@phosphor-icons/react";
import EmptyStateChip from "./EmptyStateChip";

function shortClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function lastShiftDayLabel(ymd) {
  if (!ymd) return "";
  try {
    const d = new Date(`${ymd}T12:00:00`);
    const today = new Date();
    const diffDays = Math.round((today - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return "Yesterday";
    // "Sat 6/14"
    return d.toLocaleDateString([], { weekday: "short", month: "numeric", day: "numeric" });
  } catch {
    return ymd;
  }
}

export default function ManagerHandoffCard({
  notes,
  updatedById,
  updatedAt,
  authorName,
  lastShiftNotes,       // { date, manager_notes, manager_notes_updated_by, manager_notes_updated_at } | null
  lastShiftAuthorName,  // resolved at the page level
  onEditOnZoneSheet,
}) {
  const hasNotes = !!(notes && notes.trim());
  const hasLast = !!(lastShiftNotes && (lastShiftNotes.manager_notes || "").trim());
  // 2026-02 layout pass — collapse to a single-row chip when NEITHER
  // "This Shift" nor "Last Shift" has notes. The moment either surfaces
  // content, the full card returns so the notes are properly readable.
  if (!hasNotes && !hasLast) {
    return (
      <EmptyStateChip
        icon={Note}
        tint="slate"
        label="Manager Handoff"
        message="No shift notes yet — leave one for the next manager"
        onClick={onEditOnZoneSheet}
        testid="handoff-empty-chip"
      />
    );
  }
  return (
    <section
      className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md overflow-hidden ring-1 ring-slate-100"
      data-testid="manager-handoff-card"
    >
      {/* Accent ribbon — Five Below pink → blue */}
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #ec4899 0%, #8b5cf6 50%, #0066FF 100%)" }} />
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0"
            style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)" }}
          >
            <Note weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p
              className="text-[10px] uppercase tracking-[0.22em] font-black text-pink-600 leading-none"
            >
              Manager Handoff
            </p>
            <p
              className="text-lg font-black text-slate-900 leading-tight mt-1"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
            >
              Notes from the team
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onEditOnZoneSheet}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-blue-50 text-[11px] sm:text-xs font-black text-blue-700 hover:bg-blue-100 transition-colors whitespace-nowrap"
          data-testid="handoff-edit-on-zone-sheet"
        >
          Edit on Zone Sheet
          <ArrowRight weight="bold" className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-4 sm:px-5 py-4 space-y-4">
        {/* From Last Shift — pulled from the most recent prior date
            (within 3 days) that has non-empty manager_notes. Lets the
            opening manager see Saturday's close even when Sunday was
            quiet. Hidden entirely when nothing was found in the
            window. (Wave A.6 2026-06-13.) */}
        {hasLast && (
          <div className="rounded-2xl px-3.5 py-3 bg-gradient-to-r from-indigo-50 via-violet-50 to-pink-50 ring-1 ring-indigo-100" data-testid="handoff-last-shift">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-indigo-700 leading-none mb-1.5">
              From Last Shift · {lastShiftDayLabel(lastShiftNotes.date)}
            </p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="handoff-last-body">
              {lastShiftNotes.manager_notes}
            </p>
            {(lastShiftAuthorName || lastShiftNotes.manager_notes_updated_at) && (
              <p className="mt-2 text-[10px] font-bold text-slate-500">
                {lastShiftAuthorName ? <>by <span className="text-slate-700">{lastShiftAuthorName}</span></> : null}
                {lastShiftNotes.manager_notes_updated_at ? <> · {shortClock(lastShiftNotes.manager_notes_updated_at)}</> : null}
              </p>
            )}
          </div>
        )}

        {/* This Shift — today's notes. */}
        <div data-testid="handoff-this-shift">
          <p className="text-[10px] uppercase tracking-[0.22em] font-black text-pink-700 leading-none mb-1.5">
            This Shift
          </p>
          {hasNotes ? (
            <>
              <p
                className="text-sm sm:text-[15px] text-slate-800 whitespace-pre-wrap leading-relaxed"
                data-testid="handoff-body"
              >
                {notes}
              </p>
              {(authorName || updatedAt) && (
                <p
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500"
                  data-testid="handoff-meta"
                >
                  <Clock weight="bold" className="w-3 h-3" />
                  Last updated
                  {authorName ? <> by <span className="text-slate-700">{authorName}</span></> : null}
                  {updatedAt ? <> · {shortClock(updatedAt)}</> : null}
                </p>
              )}
            </>
          ) : (
            <p
              className="text-sm text-slate-500 italic"
              data-testid="handoff-empty"
            >
              No notes for this shift yet. Tap{" "}
              <button
                type="button"
                onClick={onEditOnZoneSheet}
                className="font-bold text-blue-700 hover:underline"
                data-testid="handoff-empty-cta"
              >
                Edit on Zone Sheet
              </button>{" "}
              to leave a note for the next manager.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
